import forge from 'node-forge';

// ── Format versions ─────────────────────────────────────────────────────────
// v1 (legacy): AES-256-CBC via node-forge. Ciphertext is hex prefixed with the
//   first 6 hex chars of the IV (a checksum hint only). Key string: "<64hex>iv<32hex>".
// v2: AES-256-GCM via native WebCrypto. Ciphertext: "2|" + base64(iv12 || ct || tag16).
//   Key string: 64 hex chars (IV is per-message, stored inside the ciphertext).
// v2p: passphrase mode. Ciphertext: "2p|<iterations>|" + base64(salt16 || iv12 || ct || tag16).
//   Key derived with PBKDF2-SHA256; nothing needs to be stored anywhere.
const ivSubstringLenght = 6;

export const PBKDF2_ITERATIONS = 600000;
export const V2_PREFIX = '2|';
export const V2P_PREFIX = '2p|';

const subtle = () => {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('WebCrypto is not available in this browser');
  }
  return crypto.subtle;
};

// ── Encoding helpers ────────────────────────────────────────────────────────
function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function hexToBytesArray(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

function bytesToHexString(bytes) {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── Format detection ────────────────────────────────────────────────────────
export function detectFormat(encryptedText) {
  if (!encryptedText) return null;
  const text = encryptedText.trim();
  if (text.startsWith(V2P_PREFIX)) return 'v2p';
  if (text.startsWith(V2_PREFIX)) return 'v2';
  return 'v1';
}

// ── v2 key generation (sync, no IV — IV is per message) ─────────────────────
function generateKeyV2() {
  const keyBytes = new Uint8Array(32);
  crypto.getRandomValues(keyBytes);
  return { key: bytesToHexString(keyBytes) };
}

async function importAesKey(keyHex, usages) {
  return subtle().importKey('raw', hexToBytesArray(keyHex), { name: 'AES-GCM' }, false, usages);
}

// ── v2: AES-256-GCM with a raw key ──────────────────────────────────────────
async function encryptV2({ text, key: keyHex }) {
  if (!keyHex || keyHex.length < 64) {
    throw new Error('A 64-character hex encryption key is required');
  }
  const iv = new Uint8Array(12);
  crypto.getRandomValues(iv);
  const cryptoKey = await importAesKey(keyHex.substring(0, 64), ['encrypt']);
  const ciphertext = await subtle().encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    new TextEncoder().encode(text)
  );
  const payload = new Uint8Array(iv.length + ciphertext.byteLength);
  payload.set(iv, 0);
  payload.set(new Uint8Array(ciphertext), iv.length);
  return { encryptedText: V2_PREFIX + bytesToBase64(payload) };
}

async function decryptV2({ encryptedText, key: keyHex }) {
  const payload = base64ToBytes(encryptedText.trim().substring(V2_PREFIX.length));
  const iv = payload.slice(0, 12);
  const data = payload.slice(12);
  const cryptoKey = await importAesKey(keyHex.substring(0, 64), ['decrypt']);
  // GCM is authenticated: a wrong key throws instead of returning garbage
  const decrypted = await subtle().decrypt({ name: 'AES-GCM', iv }, cryptoKey, data);
  return new TextDecoder().decode(decrypted);
}

// ── v2p: AES-256-GCM with a passphrase-derived key (PBKDF2-SHA256) ──────────
async function deriveKeyFromPassphrase(passphrase, salt, iterations) {
  const baseKey = await subtle().importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return subtle().deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptWithPassphrase({ text, passphrase, iterations = PBKDF2_ITERATIONS }) {
  if (!passphrase) {
    throw new Error('A passphrase is required');
  }
  const salt = new Uint8Array(16);
  const iv = new Uint8Array(12);
  crypto.getRandomValues(salt);
  crypto.getRandomValues(iv);
  const cryptoKey = await deriveKeyFromPassphrase(passphrase, salt, iterations);
  const ciphertext = await subtle().encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    new TextEncoder().encode(text)
  );
  const payload = new Uint8Array(salt.length + iv.length + ciphertext.byteLength);
  payload.set(salt, 0);
  payload.set(iv, salt.length);
  payload.set(new Uint8Array(ciphertext), salt.length + iv.length);
  return { encryptedText: `${V2P_PREFIX}${iterations}|${bytesToBase64(payload)}` };
}

async function decryptWithPassphrase({ encryptedText, passphrase }) {
  const parts = encryptedText.trim().split('|');
  if (parts.length !== 3) {
    throw new Error('Invalid passphrase-protected code format');
  }
  const iterations = parseInt(parts[1], 10);
  if (!Number.isFinite(iterations) || iterations < 1 || iterations > 10000000) {
    throw new Error('Invalid passphrase-protected code format');
  }
  const payload = base64ToBytes(parts[2]);
  const salt = payload.slice(0, 16);
  const iv = payload.slice(16, 28);
  const data = payload.slice(28);
  const cryptoKey = await deriveKeyFromPassphrase(passphrase, salt, iterations);
  const decrypted = await subtle().decrypt({ name: 'AES-GCM', iv }, cryptoKey, data);
  return new TextDecoder().decode(decrypted);
}

// ── v1 (legacy) — kept so every already-printed paper stays readable ────────
function generateKey() {
  // Legacy generator (key + IV pair). New code should use generateKeyV2.
  const key = forge.util.bytesToHex(forge.random.getBytesSync(32));
  const iv = forge.util.bytesToHex(forge.random.getBytesSync(16));
  return { key, iv };
}

function encrypt({ text, key: keyHex, iv: ivHex }) {
  try {
    if (!keyHex) {
      throw new Error('Encryption key is required');
    }

    const key = typeof keyHex === 'string' ? forge.util.hexToBytes(keyHex) : keyHex;
    const iv = typeof ivHex === 'string' ? forge.util.hexToBytes(ivHex) :
               (ivHex ? ivHex : forge.random.getBytesSync(16));

    const cipher = forge.cipher.createCipher('AES-CBC', key);
    cipher.start({ iv });
    cipher.update(forge.util.createBuffer(text, 'utf8'));
    cipher.finish();

    const encrypted = cipher.output.getBytes();
    const ivSubstring = forge.util.bytesToHex(iv).substring(0, ivSubstringLenght);

    return {
      encryptedText: ivSubstring + forge.util.bytesToHex(encrypted),
      iv: forge.util.bytesToHex(iv)
    };
  } catch (error) {
    console.error('Encryption error:', error);
    throw error;
  }
}

function decrypt({ key: keyHex, iv: ivHex, encryptedText: encryptedTextWithIvSubstring }) {
    const encryptedTextHex = encryptedTextWithIvSubstring.substring(ivSubstringLenght);
    const encryptedText = forge.util.hexToBytes(encryptedTextHex);
    const key = typeof keyHex === 'string' ? forge.util.hexToBytes(keyHex) : keyHex;

    let iv;
    if (ivHex) {
      iv = typeof ivHex === 'string' ? forge.util.hexToBytes(ivHex) : ivHex;
    } else {
      throw new Error('IV must be provided for decryption');
    }

    const decipher = forge.cipher.createDecipher('AES-CBC', key);
    decipher.start({ iv });
    decipher.update(forge.util.createBuffer(encryptedText, 'raw'));
    const result = decipher.finish();

    if (result) {
      const decryptedText = decipher.output.toString('utf8');

      // AES-CBC has no authentication: wrong keys can produce valid padding but
      // garbage output, so reject output that is mostly non-printable characters.
      if (!decryptedText || decryptedText.length === 0) {
        throw new Error('Decryption failed: Invalid key or corrupted data');
      }

      const printableChars = decryptedText.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F-\x9F]/g, '').length;
      const totalChars = decryptedText.length;
      const printableRatio = totalChars > 0 ? printableChars / totalChars : 0;

      if (printableRatio < 0.8) {
        throw new Error('Decryption failed: Invalid key or corrupted data');
      }

      return decryptedText;
    } else {
      throw new Error('Decryption failed');
    }
}

export default {
  generateKey,
  generateKeyV2,
  encrypt,
  decrypt,
  encryptV2,
  decryptV2,
  encryptWithPassphrase,
  decryptWithPassphrase,
  detectFormat,
};
